
import React, { useState, useRef, useEffect } from 'react';
import { useStore } from '../context/StoreContext';
import { Bell, Search, Menu, ShoppingBag, Truck, ClipboardList, TrendingUp, AlertCircle, Trash2, RefreshCw, User, X, UserCircle, LogOut, ArrowRight, MessageSquare } from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import { useNavigate, useLocation } from 'react-router-dom';

interface HeaderProps {
  toggleSidebar: () => void;
}

const Header: React.FC<HeaderProps> = ({ toggleSidebar }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { 
    currentUser, 
    users,
    logout, 
    roleAppearances, 
    notifications, 
    clearNotifications, 
    markNotificationAsRead,
    refreshData, 
    isSyncing, 
    stopAlarm, 
    setViewInvoiceId,
    unreadChatCount,
    setIsChatOpen,
    setActiveChatUserId,
    markChatNotificationsAsRead
  } = useStore();
  const [isNotesOpen, setIsNotesOpen] = useState(false);
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [globalSearch, setGlobalSearch] = useState('');
  const notesRef = useRef<HTMLDivElement>(null);
  const profileRef = useRef<HTMLDivElement>(null);
  
  const isHome = location.pathname === '/' || location.pathname === '/dashboard' || location.pathname === '/centers-dashboard' || location.pathname === '/warehouses-dashboard' || location.pathname === '/center-dashboard';
  
  const currentDate = new Date().toLocaleDateString('ar-IQ', { timeZone: 'Asia/Baghdad', 
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });

  const getRoleDisplay = () => {
    if (!currentUser) return null;
    const role = currentUser.role;
    const appearance = roleAppearances[role] || { label: role, colorClass: 'text-cyan-600' };
    return appearance.label;
  };

  const isRestricted = currentUser?.role === 'AGENT' && !currentUser?.permissions?.viewAllWarehouses;

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (notesRef.current && !notesRef.current.contains(event.target as Node)) {
        setIsNotesOpen(false);
      }
      if (profileRef.current && !profileRef.current.contains(event.target as Node)) {
        setIsProfileOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const getNoteIcon = (type: string) => {
    switch (type) {
      case 'order': return <ShoppingBag className="text-orange-500" size={16}/>;
      case 'shipment': return <Truck className="text-blue-500" size={16}/>;
      case 'system': return <ClipboardList className="text-teal-500" size={16}/>;
      case 'sales': return <TrendingUp className="text-purple-500" size={16}/>;
      default: return <AlertCircle className="text-red-500" size={16}/>;
    }
  };

  return (
    <header className="glass border-b border-white/20 h-16 md:h-18 flex items-center justify-between px-4 md:px-6 sticky top-0 z-[20] shadow-sm">
      
      {/* Right side - Menu & Simple Title */}
      <div className="flex items-center gap-3">
        <button 
          onClick={toggleSidebar} 
          className="lg:hidden p-2 text-slate-600 hover:bg-slate-100 rounded-lg transition-all"
        >
          <Menu size={20} />
        </button>
        
        <div className="flex items-center gap-2">
          <h2 className="text-sm md:text-base font-bold text-slate-800">نظام رائد الخير</h2>
          <div className="w-1 h-1 bg-emerald-500 rounded-full animate-pulse"></div>
        </div>
      </div>

      {/* Left side actions - Notifications, Profile, Logout */}
      <div className="flex items-center gap-2 md:gap-3">
        
        {/* Notifications */}
        <div className="relative" ref={notesRef}>
          <button 
            onClick={() => {
              setIsNotesOpen(!isNotesOpen);
              stopAlarm();
            }}
            className="p-2 text-slate-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-all relative"
          >
            <Bell size={19} />
            {notifications.filter(n => !n.isRead).length > 0 && (
              <span className="absolute top-1 right-1 w-4 h-4 bg-rose-500 text-white text-[8px] font-bold flex items-center justify-center rounded-full border border-white animate-pulse">
                {notifications.filter(n => !n.isRead).length}
              </span>
            )}
          </button>

          <AnimatePresence>
            {isNotesOpen && (
              <motion.div 
                initial={{ opacity: 0, y: 10, scale: 0.95 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: 10, scale: 0.95 }}
                className="absolute top-full left-0 mt-2 w-72 md:w-80 bg-white border border-slate-100 shadow-xl rounded-2xl overflow-hidden z-[100]"
              >
                 <div className="p-4 bg-slate-50 border-b border-slate-100 flex justify-between items-center">
                    <h4 className="font-bold text-xs text-slate-700">الإشعارات</h4>
                    <button onClick={clearNotifications} className="text-[10px] text-slate-400 hover:text-rose-500 transition-colors">
                      مسح الكل
                    </button>
                 </div>
                 <div className="max-h-80 overflow-y-auto no-scrollbar">
                    {notifications.length > 0 ? notifications.map(note => (
                      <div 
                        key={note.id} 
                        onClick={() => {
                          markNotificationAsRead(note.id);
                          const isChatNote = (note.title && (note.title.includes('رسالة') || note.title.includes('💬'))) ||
                                             (note.message && (note.message.includes('رسالة') || note.message.includes('💬')));
                          if (isChatNote) {
                            setIsNotesOpen(false);
                            setIsChatOpen(true);
                            if (['SUPER_ADMIN', 'ADMIN', 'EDITOR'].includes(currentUser?.role || '')) {
                              const matchedUser = users.find(u => note.title.includes(u.name) || note.message.includes(u.name));
                              if (matchedUser) {
                                setActiveChatUserId(matchedUser.id);
                              }
                            }
                          } else if (note.invoiceId) {
                            setViewInvoiceId(note.invoiceId);
                            setIsNotesOpen(false);
                          }
                        }}
                        className={`p-4 hover:bg-slate-50 border-b border-slate-50 last:border-0 transition-colors cursor-pointer flex gap-3 text-right ${!note.isRead ? 'bg-indigo-50/40 border-r-4 border-r-indigo-500' : ''}`}
                      >
                         <div className="shrink-0">{getNoteIcon(note.type)}</div>
                         <div className="flex-1">
                            <p className="text-[11px] font-bold text-slate-800">{note.title}</p>
                            <p className="text-[10px] text-slate-500 line-clamp-2">{note.message}</p>
                         </div>
                      </div>
                    )) : (
                      <div className="p-8 text-center text-slate-400">
                        <p className="text-[10px]">لا توجد إشعارات</p>
                      </div>
                    )}
                 </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Chat Notification */}
        <div className="relative">
          <button 
            onClick={() => {
              setIsChatOpen(true);
              markChatNotificationsAsRead().catch(() => {});
            }}
            className="p-2 text-slate-500 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-all relative"
          >
            <MessageSquare size={19} />
            {unreadChatCount > 0 && (
              <span className="absolute top-1 right-1 w-4 h-4 bg-rose-500 text-white text-[8px] font-bold flex items-center justify-center rounded-full border border-white">
                {unreadChatCount}
              </span>
            )}
          </button>
        </div>

        <div className="w-px h-6 bg-slate-100 mx-1"></div>

        {/* User Profile */}
        <button 
          onClick={() => currentUser && navigate(`/employees?userId=${currentUser.id}`)}
          className="w-8 h-8 md:w-9 md:h-9 rounded-full bg-slate-100 overflow-hidden border border-white shadow-sm ring-1 ring-slate-100 hover:ring-primary-400 transition-all"
          title={currentUser?.name}
        >
          {currentUser?.profileImage ? (
            <img src={currentUser.profileImage} alt="Profile" className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-slate-500 font-bold text-xs bg-slate-200">
              {currentUser?.name?.charAt(0) || <User size={14} />}
            </div>
          )}
        </button>

        {/* Logout */}
        <button 
           onClick={logout}
           className="p-2 text-slate-400 hover:text-rose-500 transition-colors"
           title="تسجيل الخروج"
        >
           <LogOut size={19} />
        </button>
      </div>
    </header>
  );
};

export default Header;
